import React from 'react';
import { Column } from 'react-table';
import { isNil } from 'lodash';
import { ColumnUtilType } from '../../../../../types/common/table';
import { PRODUCT_STATUS_V2, ProductItemV2 } from '../../../../../types/product';
import { openNewWindow, setDash, setDetailPageUrl, setNumberWithComma } from '../../../../../lib/common';
import { BRAND_DETAIL_URL_PATH, PRODUCT_DETAIL_URL_PATH } from '../../../../../constants/service/common/urlConstants';
import moment from '../../../../../lib/moment';
import { DATE_FORMAT } from '../../../../../constants/service/common/utilConstants';
import TableFilterInput from '../../../../../components/table/TableFilterInput';
import Anchor from '../../../../../components/link/Anchor';
import { WidgetProductFilterOption } from '../../../../../types/widget';
import TableColumnModalImg from '../../../../../components/table/TableColumModalImg';
import MainCategorySelect from './MainCategorySelect';
import { MainCategoryItemV2 } from '../../../../../types/category';
import { PRODUCT_STATUS_LABEL_V2 } from '../../../../../constants/service/app/productConstantsV2';

type ProductColumnType = Pick<
  ProductItemV2,
  | 'imageUrl'
  | 'brandName'
  | 'id'
  | 'mainCategoryList'
  | 'name'
  | 'originPrice'
  | 'discountPrice'
  | 'discountRate'
  | 'discountPeriod'
  | 'salesPrice'
  | 'createDate'
>;

export const productReactTableColumns = (
  data: ColumnUtilType<ProductColumnType, WidgetProductFilterOption>,
): (Column<ProductColumnType> & { fixed?: 'left' | 'right' })[] => {
  const { filterOption = {}, onFilter } = data;

  return [
    {
      accessor: 'imageUrl',
      Header: '이미지',
      width: 100,
      fixed: 'left',
      Cell: (row) => <TableColumnModalImg url={row.value} />,
    },
    {
      accessor: 'brandName',
      Header: '브랜드명',
      filterable: true,
      width: 200,
      Filter: () => (
        <TableFilterInput option={filterOption.brandName} onFilter={(brandName: string) => onFilter({ brandName })} />
      ),
    },
    {
      accessor: 'brandId',
      Header: '브랜드 ID',
      filterable: true,
      width: 70,
      Cell: (row) => (
        <Anchor
          color="primary"
          isUnderLine
          text={row.value}
          onClick={() => openNewWindow(setDetailPageUrl(BRAND_DETAIL_URL_PATH, row.value))}
        />
      ),
      Filter: () => (
        <TableFilterInput
          type="number"
          option={filterOption.brandId}
          onFilter={(brandId: string) => onFilter({ brandId: isNil(brandId) ? brandId : Number(brandId) })}
        />
      ),
    },
    {
      accessor: 'id',
      Header: '상품 번호',
      filterable: true,
      Cell: (row) => (
        <Anchor
          color="primary"
          isUnderLine
          text={row.value}
          onClick={() => openNewWindow(setDetailPageUrl(PRODUCT_DETAIL_URL_PATH, row.value))}
        />
      ),
      Filter: () => (
        <TableFilterInput
          type="number"
          option={filterOption.id}
          onFilter={(id: string) => onFilter({ id: isNil(id) ? id : Number(id) })}
        />
      ),
    },
    {
      accessor: 'mainCategoryList',
      Header: '1차 카테고리',
      width: 500,
      filterable: true,
      Filter: () => (
        <MainCategorySelect
          value={filterOption.mainCategoryList || null}
          onChange={(mainCategoryId: number | null) => onFilter({ mainCategoryList: mainCategoryId })}
        />
      ),
      Cell: (row) => <div>{row.value?.map((category: MainCategoryItemV2) => category.name).join(', ')}</div>,
    },
    {
      accessor: 'name',
      Header: '상품명',
      resizable: true,
      filterable: true,
      width: 200,
      Filter: () => <TableFilterInput option={filterOption.name} onFilter={(name: string) => onFilter({ name })} />,
    },
    {
      accessor: 'status',
      Header: '상태',
      width: 100,
      Cell: (row) => setDash(PRODUCT_STATUS_LABEL_V2[row.value as PRODUCT_STATUS_V2]),
    },
    {
      accessor: 'originPrice',
      Header: '판매가 (원)',
      sortable: true,
      Cell: (row) => setNumberWithComma(row.value),
    },
    {
      accessor: 'discountPrice',
      Header: '할인가 (원)',
      sortable: true,
      Cell: (row) => setNumberWithComma(row.value),
    },
    {
      accessor: 'discountRate',
      Header: '할인율 (%)',
      sortable: true,
      Cell: (row) => setNumberWithComma(row.value),
    },
    {
      accessor: 'discountPeriod',
      Header: '할인기간',
      width: 300,
      Cell: (row) =>
        row.value?.startDate && row.value?.endDate
          ? `${moment(row.value.startDate).format(DATE_FORMAT.FULL_DASH_FORMAT)} ~ ${moment(row.value.endDate).format(
              DATE_FORMAT.FULL_DASH_FORMAT,
            )}`
          : '-',
    },
    {
      accessor: 'salesPrice',
      Header: '실판매가 (원)',
      sortable: true,
      Cell: (row) => setNumberWithComma(row.value),
    },
    {
      accessor: 'createDate',
      Header: '등록일',
      sortable: true,
      width: 90,
      Cell: (row) => moment(row.value).format(DATE_FORMAT.YMD_DASH_FORMAT),
    },
  ];
};
